import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileImage, FileText, Film, Globe, Upload, ArrowUpRight, ArrowLeft, CalendarClock, Eye, EyeOff, MoreHorizontal, ChevronRight, Pin, PinOff, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative, readFileAsDataURL, fmtBytes } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { toggleProjectWatch, refreshWatchList } from "@/components/WatchListHeaderButton";
import ProjectActivityTab from "@/components/ProjectActivityTab";
import RunSOPButton from "@/components/sops/RunSOPButton";
import ProjectMembersTab from "@/components/ProjectMembersTab";
import ProjectStakeholdersTab from "@/components/ProjectStakeholdersTab";
import ProjectMilestonesTab from "@/components/ProjectMilestonesTab";
import LogModule from "@/components/LogModule";
import ProjectMeetingsTab from "@/components/ProjectMeetingsTab";
import { ProjectDashboardTab, ProjectStatusUpdatesTab, ProjectTasksSubTab, ProjectIssuesSubTab, ProjectGoalsSubTab } from "@/components/project/ProjectSubTabs";
import ProjectDashboardPlus from "@/components/project/ProjectDashboardPlus";
import ProjectFilesTab from "@/components/project/ProjectFilesTab";
import { EntityDiagramsTab } from "@/components/diagrams/EntityDiagramsTab";
import { EntityDocumentsTab } from "@/components/documents/EntityDocumentsTab";
import ProjectTicketsTab from "@/components/project/ProjectTicketsTab";
import ProjectOverviewTab from "@/components/ProjectOverviewTab";
import ProjectQualityTab from "@/components/ProjectQualityTab";
import ProjectVarianceTab from "@/components/ProjectVarianceTab";
import AttachmentsTab from "@/components/AttachmentsTab";
import EmailsTab from "@/components/EmailsTab";
import AuditFooter from "@/components/AuditFooter";
import AuditTrailPanel from "@/components/AuditTrailPanel";
// R69c · Project-scoped billing dashboard + drill-down.
import BillingDashboard from "@/components/billing/BillingDashboard";
import TaskBillingView from "@/components/tasks/TaskBillingView";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import { useAuth } from "@/lib/auth";
import EntityQuickSwitcher from "@/components/EntityQuickSwitcher";
import ArchiveToggle from "@/components/ArchiveToggle";
import FloatingProjectNavigator, { ProjectSortChip, useProjectSort, sortProjects } from "@/components/project/FloatingProjectNavigator";
import ProjectLinkIcons from "@/components/ProjectLinkIcons";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export default function ProjectDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeWorkspaceId } = useAuth();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [myPerms, setMyPerms] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const fileRef = useRef(null);
  // R69c + R148g · Feb 2026 — `task.view.billing` is a standalone gate
  // that unlocks the Billing subtab without granting the broader
  // finance.* perms. Kept OR-chained with the old finance perms for
  // backward compat.
  const { hasPerm, isAdminRole, ready: permsReady } = useWorkspacePerms();
  const canBilling = hasPerm("task.view.billing") ||
    hasPerm("finance.view") || hasPerm("finance.manage") ||
    hasPerm("workspace.admin") || hasPerm("workspace.manage");

  // R138o · Per-subtab visibility. Opt-in restriction: role with NO
  // `project.tab.*.view` codes sees every tab (backward-compat); as
  // soon as ANY code is granted, only granted tabs render. Admin-tier
  // roles always see every tab. Billing has an extra AND with
  // finance.view (existing behaviour).
  // R148d · Feb 2026 — Tasks leads the list so it becomes the landing
  // tab on every project open. PMs work in Tasks 80% of the time —
  // keep Dashboard second for quick health-check switching.
  // Feb 2026 R293p · Grouped logical ordering so related tabs sit
  // next to each other:
  //   Execution        → Tasks, Dashboard, Overview, Milestones
  //   Communication    → Meetings, Status Updates, Activity, Emails
  //   People           → Members, Stakeholders
  //   Deliverables     → Review items, Tickets, Files, Documents, Diagrams
  //   Business + audit → Billing, Goals, Log, Quality, Effort variance
  const PROJECT_TABS_ORDER = useMemo(() => ([
    { value: "tasks",        label: "Tasks",           perm: "project.tab.tasks.view" },
    { value: "dashboard",    label: "Dashboard",       perm: "project.tab.dashboard.view" },
    { value: "overview",     label: "Overview",        perm: "project.tab.overview.view" },
    { value: "milestones",   label: "Milestones",      perm: "project.tab.milestones.view" },
    { value: "meetings",     label: "Meetings",        perm: "project.tab.meetings.view" },
    { value: "billing",      label: "Billing",         perm: "project.tab.billing.view", orPerm: "task.view.billing", requires: () => canBilling },
    { value: "updates",      label: "Status Updates",  perm: "project.tab.updates.view" },
    { value: "members",      label: "Members",         perm: "project.tab.members.view" },
    { value: "stakeholders", label: "Stakeholders",    perm: "project.tab.stakeholders.view" },
    { value: "goals",        label: "Goals",           perm: "project.tab.goals.view" },
    { value: "tickets",      label: "Tickets",         perm: "project.tab.tickets.view" },
    { value: "items",        label: "Review items",    perm: "project.tab.items.view" },
    { value: "files",        label: "Files",           perm: "project.tab.files.view" },
    { value: "documents",    label: "Documents",       perm: "project.tab.documents.view" },
    { value: "diagrams",     label: "Diagrams",        perm: "project.tab.diagrams.view" },
    { value: "emails",       label: "Emails",          perm: "project.tab.emails.view" },
    { value: "activity",     label: "Activity",        perm: "project.tab.activity.view" },
    { value: "log",          label: "Log",             perm: "project.tab.log.view" },
    { value: "quality",      label: "Quality",         perm: "project.tab.quality.view" },
    { value: "variance",     label: "Effort variance", perm: "project.tab.variance.view" },
  ]), [canBilling]);
  const visibleTabs = useMemo(() => {
    const base = PROJECT_TABS_ORDER.filter((t) => !t.requires || t.requires());
    if (!permsReady) return base;                    // fail-open while loading
    if (isAdminRole) return base;                    // admins see everything
    // A tab is "granted" if the user has `perm` OR the optional
    // `orPerm` fallback (currently only the Billing tab uses this so a
    // single `task.view.billing` grant lights it up).
    const grants = (t) => hasPerm(t.perm) || (t.orPerm && hasPerm(t.orPerm));
    const anyExplicit = base.some(grants);
    if (!anyExplicit) return base;                   // no codes granted → backward-compat
    return base.filter(grants);                      // opt-in allow-list
  }, [PROJECT_TABS_ORDER, permsReady, isAdminRole, hasPerm]);
  const visibleValues = useMemo(() => new Set(visibleTabs.map((t) => t.value)), [visibleTabs]);

  // R138bh · Controlled active tab via `?tab=` so the Project quick
  // switcher can preserve the current sub-tab across project changes.
  const urlTab = searchParams.get("tab");
  const defaultTabValue = visibleTabs[0]?.value || "dashboard";
  const activeTab = urlTab && visibleValues.has(urlTab) ? urlTab : defaultTabValue;
  const setActiveTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === defaultTabValue) next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: false });
  };

  // R138bh · Fetch sibling projects on demand for the quick switcher.
  // Feb 2026 · Server-side sort via `?sort=&dir=` so the navigator
  // doesn't ship the entire list client-side for 500+ project
  // workspaces. The whitelist (name/open_tasks/billed_tasks/…) is
  // enforced in the backend; anything else falls back to created_at.
  const fetchSiblingProjects = async () => {
    if (!activeWorkspaceId) return [];
    const { data } = await api.get(
      `/workspaces/${activeWorkspaceId}/projects?limit=500&sort=${encodeURIComponent(sortKey)}&dir=${sortDir}`
    );
    const rows = Array.isArray(data) ? data : (data?.rows || data?.projects || []);
    return rows.map((p) => ({
      id: p.id,
      name: p.name,
      code: p.code || p.project_code || "",
      subtitle: p.client_name || p.client?.name || "",
    }));
  };

  // Feb 2026 · Siblings + sort state powering the FloatingProjectNavigator.
  // Keeps the full project rows (not just id/name) so we can sort by
  // open_tasks / pm_name / etc. without re-fetching per cycle.
  const [siblings, setSiblings] = useState([]);
  const { sortKey, sortDir, setKey: setSortKey, toggleDir: toggleSortDir } = useProjectSort(activeWorkspaceId);
  const [navDismissed, setNavDismissed] = useState(() => {
    try { return localStorage.getItem(`projects.navigator.dismissed.${activeWorkspaceId || "_"}`) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(`projects.navigator.dismissed.${activeWorkspaceId || "_"}`, navDismissed ? "1" : "0"); }
    catch { /* quota */ }
  }, [navDismissed, activeWorkspaceId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!activeWorkspaceId) return;
      try {
        const { data } = await api.get(
          `/workspaces/${activeWorkspaceId}/projects?limit=500&sort=${encodeURIComponent(sortKey)}&dir=${sortDir}`
        );
        const rows = Array.isArray(data) ? data : (data?.rows || data?.projects || []);
        if (alive) setSiblings(rows);
      } catch { if (alive) setSiblings([]); }
    })();
    return () => { alive = false; };
  }, [activeWorkspaceId, sortKey, sortDir]);
  const sortedSiblings = useMemo(
    () => sortProjects(siblings, sortKey, sortDir),
    [siblings, sortKey, sortDir]
  );


  const load = async () => {
    const [p, i, perms, pm] = await Promise.all([
      api.get(`/projects/${pid}`),
      api.get(`/projects/${pid}/review-items`),
      api.get(`/projects/${pid}/my-permissions`).catch(() => ({ data: { permissions: [] } })),
      api.get(`/projects/${pid}/members`).catch(() => ({ data: [] })),
    ]);
    setProject(p.data);
    setItems(i.data);
    setMyPerms(perms.data?.permissions || []);
    setProjectMembers(pm.data || []);
  };

  useEffect(() => { load(); }, [pid]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    const item_type = file.type.startsWith("image/")
      ? "image"
      : file.type === "application/pdf"
      ? "pdf"
      : file.type.startsWith("video/")
      ? "video"
      : null;
    if (!item_type) return toast.error("Supported file types: images, PDFs, and videos");
    // Hard guard: cap video uploads at 50MB so users see a clean toast instead of a 413/timeout.
    const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
    if (item_type === "video" && file.size > MAX_VIDEO_BYTES) {
      return toast.error(`Video too large: ${(file.size / 1024 / 1024).toFixed(1)} MB. Max 50 MB.`);
    }
    setBusy(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      const { data } = await api.post(`/projects/${pid}/review-items`, {
        title: title || file.name,
        item_type,
        file_data_url: dataUrl,
        file_size: file.size,
        mime_type: file.type,
      });
      setOpen(false);
      setTitle(""); setFile(null);
      toast.success("Uploaded");
      nav(`/app/review-items/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Upload failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Feb 2026 · Ultra-compact single-row header. The breadcrumb
          folds into the eyebrow slot of the dense PageHeader so the
          whole top strip reads one line on desktop and two on mobile.
          Title stays small (text-base/lg) so it never dominates the
          screen — the real estate is for the tabs + table below. */}
      <PageHeader
        eyebrow={
          <nav aria-label="Breadcrumb" data-testid="project-breadcrumb"
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            {/* Feb 2026 R293p · Icon-only back-to-Projects affordance so
                the breadcrumb reclaims horizontal space for the title. */}
            <Link to="/app/projects"
                   className="inline-flex items-center justify-center h-6 w-6 border border-transparent hover:border-border hover:text-primary"
                   title="Back to Projects"
                   aria-label="Back to Projects"
                   data-testid="project-back-all-link">
              <ArrowLeft className="w-3.5 h-3.5" />
            </Link>
            {project?.client_id && project.client?.name && (
              <>
                <ChevronRight className="w-3 h-3 opacity-40" />
                <Link to={`/app/clients/${project.client_id}`}
                       className="truncate max-w-[14ch] hover:text-primary"
                       data-testid="project-back-link">
                  {project.client.name}
                </Link>
              </>
            )}
          </nav>
        }
        title={project ? (
          /* Feb 2026 R293p · Title doubles as the project switcher so
              we don't duplicate the project name twice on the header
              (big title + "PROJECT: xxx" chip on the right). Clicking
              the title opens a searchable sibling list. */
          <EntityQuickSwitcher
            currentId={pid}
            currentName={project?.name}
            fetchItems={fetchSiblingProjects}
            buildPath={(id) => `/app/projects/${id}`}
            label="Project"
            testidBase="project-switcher"
            renderTrigger={({ toggle, currentName }) => (
              <button
                type="button"
                onClick={toggle}
                className="group inline-flex items-center gap-1.5 max-w-full text-left hover:text-primary transition-colors"
                title={`Switch project (current: ${currentName || "—"})`}
                data-testid="project-switcher-trigger"
              >
                <span className="truncate">{currentName || "—"}</span>
                <ChevronsUpDown className="w-4 h-4 opacity-40 group-hover:opacity-100 shrink-0" />
              </button>
            )}
          />
        ) : "—"}
        description={project?.description || null}
        density="dense"
        testid="project-detail-header"
        meta={project && (
          <>
            {project?.brand?.name && (
              <span className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 bg-muted/30" data-testid="project-meta-brand">
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Brand</span>
                <span className="font-medium truncate max-w-[16ch]">{project.brand.name}</span>
              </span>
            )}
            {project?.status && (
              <span className="inline-block border border-border px-1.5 py-0.5 bg-muted/30 uppercase tracking-widest text-[10px]" data-testid="project-meta-status">
                {project.status.replace("_", " ")}
              </span>
            )}
            <DaysToGoBadge dueDate={project?.due_date} status={project?.status} />
            <ProjectTasksProgressChip wsId={project?.workspace_id} projectId={pid} />
            {/* Quick-link service icons so the group WhatsApp / Drive / Figma /
                 etc. are always a click away from the header. */}
            <ProjectLinkIcons links={project?.project_links || []} max={4} size="xs" testidBase="project-header-link" />
          </>
        )}
        actions={
          <div className="flex gap-1.5 items-center flex-wrap">
            <ProjectDetailWatchToggle projectId={pid} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline"
                        className="rounded-none h-7 px-2"
                        data-testid="project-more-actions">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Project actions
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <ProjectSortChip
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onChangeKey={setSortKey}
                    onToggleDir={toggleSortDir}
                    testid="project-sort-chip"
                  />
                </div>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 flex flex-col gap-1.5">
                  <AuditTrailPanel collection="projects" recordId={pid} testid="project-audit-trail" />
                  <RunSOPButton linkKind="project" linkId={pid} testKey={`project-${pid}`} />
                  {project && (isAdminRole || hasPerm("project.archive") || hasPerm("workspace.manage")) && (
                    <ArchiveToggle
                      kind="project"
                      entityId={pid}
                      archivedAt={project.archived_at}
                      onDone={load}
                    />
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />
      <div className="px-3 sm:px-4 md:px-6 py-2">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <ProjectTabsBar
            tabs={visibleTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          {visibleValues.has("dashboard") && (
          <TabsContent value="dashboard" className="mt-2">
            {project && (
              <div className="space-y-3">
                <ProjectDashboardTab projectId={pid} project={project} />
                <ProjectDashboardPlus projectId={pid} project={project} />
              </div>
            )}
          </TabsContent>
          )}
          {visibleValues.has("updates") && (
          <TabsContent value="updates" className="mt-2">
            {project && <ProjectStatusUpdatesTab projectId={pid} project={project} projectMembers={projectMembers} />}
          </TabsContent>
          )}
          {visibleValues.has("tasks") && (
          <TabsContent value="tasks" className="mt-2">
            {project && <ProjectTasksSubTab projectId={pid} project={project} />}
          </TabsContent>
          )}
          {visibleValues.has("billing") && canBilling && (
            <TabsContent value="billing" className="mt-2">
              {project && (
                <div className="space-y-4" data-testid="project-billing-tab">
                  <BillingDashboard wsId={project.workspace_id} projectId={pid} />
                  <div className="border-t border-border pt-4">
                    <div className="label-eyebrow mb-3">Billable tasks in this project</div>
                    <TaskBillingView wsId={project.workspace_id} projectId={pid} />
                  </div>
                </div>
              )}
            </TabsContent>
          )}
          {visibleValues.has("tickets") && (
          <TabsContent value="tickets" className="mt-2">
            {project && <ProjectTicketsTab projectId={pid} project={project} />}
          </TabsContent>
          )}
          {visibleValues.has("goals") && (
          <TabsContent value="goals" className="mt-2">
            {project && <ProjectGoalsSubTab projectId={pid} project={project} />}
          </TabsContent>
          )}
          {visibleValues.has("overview") && (
          <TabsContent value="overview" className="mt-2">
            {project && <ProjectOverviewTab project={project} projectMembers={projectMembers} onSaved={load} />}
          </TabsContent>
          )}
          {visibleValues.has("items") && (
          <TabsContent value="items" className="mt-2">
            {/* R138o · Buttons moved from header — they only apply to the Review items tab */}
            <div className="flex gap-2 items-center justify-end mb-4 flex-wrap" data-testid="review-items-toolbar">
              <WebsiteAddDialog projectId={pid} onCreated={(id) => nav(`/app/review-items/${id}`)} />
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-none" data-testid="project-upload-btn"><Upload className="w-4 h-4 mr-2" />Upload review item</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Upload a review item</DialogTitle></DialogHeader>
                  <form onSubmit={onUpload} className="space-y-4">
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Title (optional)</Label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Hero visual v1" data-testid="upload-title-input" />
                    </div>
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">File (image, PDF, or video — max 50 MB for video)</Label>
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*,application/pdf,video/*"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm border border-input file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-foreground file:text-background file:font-bold file:uppercase file:text-xs file:tracking-widest"
                        data-testid="upload-file-input"
                        required
                      />
                      {file && <div className="text-xs text-muted-foreground mt-2">{file.name} · {fmtBytes(file.size)}</div>}
                    </div>
                    <Button type="submit" disabled={busy} className="w-full rounded-none" data-testid="upload-submit-btn">{busy ? "Uploading…" : "Upload"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {items.length === 0 ? (
              <EmptyState
                title="No review items yet"
                description="Upload an image or PDF to start collecting feedback and approvals."
                icon={FileImage}
                testid="project-empty"
                action={<Button onClick={() => setOpen(true)} className="rounded-none" data-testid="project-empty-upload-btn">Upload first item</Button>}
              />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {items.map((it) => (
                  <Link key={it.id} to={`/app/review-items/${it.id}`} className="border border-border hover:border-primary/40 group transition flex flex-col" data-testid={`review-item-card-${it.id}`}>
                    <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                      <div className="absolute top-2 left-2 flex gap-2">
                        {it.item_type === "image" ? <FileImage className="w-4 h-4 text-muted-foreground" />
                          : it.item_type === "video" ? <Film className="w-4 h-4 text-muted-foreground" />
                          : it.item_type === "website" ? <Globe className="w-4 h-4 text-muted-foreground" />
                          : <FileText className="w-4 h-4 text-muted-foreground" />}
                        <span className="label-eyebrow">{it.item_type}</span>
                      </div>
                      <div className="absolute top-2 right-2">
                        <span className={
                          it.approval_status === "approved" ? "chip chip-approved" :
                          it.approval_status === "changes_requested" ? "chip chip-changes" :
                          "chip chip-pending"
                        }>
                          {it.approval_status === "changes_requested" ? "changes" : it.approval_status}
                        </span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-mono text-3xl text-muted-foreground/60">v{it.current_version_number}</span>
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="font-semibold truncate" style={{ fontFamily: "var(--font-heading)" }}>{it.title}</div>
                        <div className="text-xs text-muted-foreground">{it.comment_count} comments · {fmtRelative(it.created_at)}</div>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
          )}
          {visibleValues.has("activity") && (
          <TabsContent value="activity" className="mt-2">
            {project && (
              <ProjectActivityTab projectId={pid} projectDigestFrequency={project.digest_frequency} />
            )}
          </TabsContent>
          )}
          {visibleValues.has("milestones") && (
          <TabsContent value="milestones" className="mt-2">
            <ProjectMilestonesTab projectId={pid} projectMembers={projectMembers} myPerms={myPerms} />
          </TabsContent>
          )}
          {visibleValues.has("meetings") && (
          <TabsContent value="meetings" className="mt-2">
            {project && (
              <ProjectMeetingsTab projectId={pid} workspaceId={project.workspace_id} projectMembers={projectMembers} myPerms={myPerms} />
            )}
          </TabsContent>
          )}
          {visibleValues.has("log") && (
          <TabsContent value="log" className="mt-2">
            <LogModule scope="project" scopeId={pid} defaults={{ project_id: pid, client_id: project?.client_id, brand_id: project?.brand_id }} testid="project-log" />
          </TabsContent>
          )}
          {visibleValues.has("quality") && (
          <TabsContent value="quality" className="mt-2">
            <ProjectQualityTab projectId={pid} />
          </TabsContent>
          )}
          {visibleValues.has("variance") && (
          <TabsContent value="variance" className="mt-2">
            {project && <ProjectVarianceTab project={project} />}
          </TabsContent>
          )}
          {visibleValues.has("members") && (
          <TabsContent value="members" className="mt-2">
            {project && (
              <ProjectMembersTab projectId={pid} workspaceId={project.workspace_id} myPerms={myPerms} />
            )}
          </TabsContent>
          )}
          {visibleValues.has("stakeholders") && (
          <TabsContent value="stakeholders" className="mt-2">
            {project && <ProjectStakeholdersTab project={project} />}
          </TabsContent>
          )}
          {visibleValues.has("emails") && (
          <TabsContent value="emails" className="mt-2">
            <EmailsTab entityType="project" entityId={pid} embedded />
          </TabsContent>
          )}
          {visibleValues.has("files") && (
          <TabsContent value="files" className="mt-2">
            <ProjectFilesTab projectId={pid} />
          </TabsContent>
          )}
          {visibleValues.has("diagrams") && (
          <TabsContent value="diagrams" className="mt-2">
            <EntityDiagramsTab entityType="project" entityId={pid} entityName={project?.name || "this project"} />
          </TabsContent>
          )}
          {visibleValues.has("documents") && (
          <TabsContent value="documents" className="mt-2">
            <EntityDocumentsTab entityType="project" entityId={pid} entityName={project?.name || "this project"} />
          </TabsContent>
          )}
        </Tabs>
        {project && <AuditFooter record={project} testid="project-audit-footer" />}
      </div>
      {!navDismissed && siblings.length > 1 && (
        <FloatingProjectNavigator
          wsId={activeWorkspaceId}
          projects={siblings}
          currentId={pid}
          sortKey={sortKey}
          sortDir={sortDir}
          onChangeKey={setSortKey}
          onToggleDir={toggleSortDir}
          onDismiss={() => setNavDismissed(true)}
        />
      )}
    </>
  );
}


function WebsiteAddDialog({ projectId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    let candidate = (url || "").trim();
    if (!/^https?:\/\//i.test(candidate)) candidate = `https://${candidate}`;
    try {
      new URL(candidate);
    } catch {
      return toast.error("Please enter a valid URL (https://example.com)");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/review-items`, {
        title: (title || candidate).slice(0, 120),
        item_type: "website",
        file_data_url: candidate,
        file_size: 0,
        mime_type: "text/html",
      });
      toast.success("Website review item created");
      setOpen(false);
      setTitle(""); setUrl("");
      onCreated && onCreated(data.id);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not create website item"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none" data-testid="project-add-website-btn">
          <Globe className="w-4 h-4 mr-2" /> Add website
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a website for review</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-eyebrow mb-2 inline-block">URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/landing"
              data-testid="website-url-input-create"
              required
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              The site will load in a preview frame. Many sites refuse to embed (X-Frame-Options/CSP) — in that case you can still annotate from screenshots.
            </p>
          </div>
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Homepage v3" data-testid="website-title-input" />
          </div>
          <Button type="submit" disabled={busy || !url.trim()} className="w-full rounded-none" data-testid="website-create-submit-btn">
            {busy ? "Creating…" : "Create"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* R138o · Compact "days to go" badge for the Project Detail header.
   Colour progresses from muted → primary (>14d away) → amber (7d) →
   red (overdue). Skipped entirely when no due_date is set or when the
   project is already closed / archived. */
function DaysToGoBadge({ dueDate, status }) {
  const info = useMemo(() => {
    if (!dueDate) return null;
    if (status && ["completed", "archived", "cancelled"].includes(status)) return null;
    const due = new Date(dueDate + (dueDate.length === 10 ? "T23:59:59" : ""));
    if (isNaN(due.getTime())) return null;
    const now = new Date();
    const diffMs = due.getTime() - now.getTime();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return { days, dueDate };
  }, [dueDate, status]);
  if (!info) return null;
  const { days } = info;
  const overdue = days < 0;
  const dueToday = days === 0;
  let cls = "border-border bg-background text-foreground/80";
  let label;
  if (overdue) {
    cls = "border-destructive/40 bg-destructive/10 text-destructive";
    label = `${Math.abs(days)}d overdue`;
  } else if (dueToday) {
    cls = "border-destructive/40 bg-destructive/10 text-destructive font-semibold";
    label = "Due today";
  } else if (days <= 7) {
    cls = "border-orange-400/40 bg-orange-100/40 text-orange-800 dark:text-orange-300 font-semibold";
    label = `${days}d to go`;
  } else if (days <= 30) {
    cls = "border-primary/30 bg-primary/5 text-primary";
    label = `${days}d to go`;
  } else {
    label = `${days}d to go`;
  }
  const nice = new Date(dueDate).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border ${cls}`}
      title={`Project due ${nice}`}
      data-testid="project-days-to-go-badge"
    >
      <CalendarClock className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className="opacity-60 hidden sm:inline">· {nice}</span>
    </span>
  );
}

/* R148f · Feb 2026 — Project-header progress chip
   Uses the same `tasks-with-risk` endpoint the Tasks list uses so the
   count matches what the user sees when they open the Tasks tab. */
function ProjectTasksProgressChip({ wsId, projectId }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!wsId || !projectId) return;
    api.get(`/workspaces/${wsId}/tasks-with-risk`, { params: { project_id: projectId, lean: "1", limit: 500 } })
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : [];
        const total = rows.length;
        const done = rows.filter((t) => t.status === "done").length;
        setState({ done, total });
      })
      .catch(() => setState(null));
  }, [wsId, projectId]);
  if (!state || state.total === 0) return null;
  const pct = state.done / state.total;
  const cls = pct >= 1
    ? "border-emerald-500 text-emerald-700 bg-emerald-50"
    : pct >= 0.5
      ? "border-amber-500 text-amber-700 bg-amber-50"
      : "border-border text-muted-foreground bg-muted/40";
  return (
    <span
      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
      title={`${state.done} of ${state.total} tasks done`}
      data-testid="project-header-progress-chip"
    >
      {state.done}/{state.total} done
    </span>
  );
}





// R158h · Feb 2026 — Watch toggle chip in the Project Detail header.
// Shows a live watcher count so a PM knows who else is watching the
// project. Uses the same acq:watch-refresh event bus as the app-wide
// watch list, so toggles here sync into the header badge instantly.
function ProjectDetailWatchToggle({ projectId }) {
  const { activeWorkspaceId } = useAuth();
  const [watched, setWatched] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const sync = useMemo(() => async () => {
    if (!activeWorkspaceId || !projectId) return;
    try {
      const [{ data: ids }, { data: w }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/project-watch/ids`),
        api.get(`/workspaces/${activeWorkspaceId}/project-watch/${projectId}/watchers`).catch(() => ({ data: { count: 0 } })),
      ]);
      setWatched((ids?.project_ids || []).includes(projectId));
      setCount(w?.count || 0);
    } catch { /* silent */ }
  }, [activeWorkspaceId, projectId]);

  useEffect(() => { sync(); }, [sync]);
  useEffect(() => {
    const h = () => sync();
    window.addEventListener("acq:watch-refresh", h);
    return () => window.removeEventListener("acq:watch-refresh", h);
  }, [sync]);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    setWatched((v) => !v);
    setCount((c) => Math.max(0, c + (watched ? -1 : 1)));
    await toggleProjectWatch(activeWorkspaceId, projectId, watched);
    refreshWatchList();
    setBusy(false);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`relative inline-flex items-center justify-center h-7 w-7 border ${watched ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted text-muted-foreground hover:text-foreground"}`}
      title={watched
        ? `Watching this project${count > 0 ? ` (${count} watcher${count === 1 ? "" : "s"})` : ""} — click to unwatch`
        : "Add project to watch list"}
      aria-label={watched ? "Watching this project" : "Add project to watch list"}
      aria-pressed={watched}
      data-testid="project-detail-watch-toggle"
    >
      {watched ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {count > 0 && (
        <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[9px] font-semibold bg-primary text-primary-foreground rounded-full border border-background">
          {count}
        </span>
      )}
    </button>
  );
}


/* Feb 2026 R293i · ProjectTabsBar — responsive, overflow-aware tab bar
 * for the project detail. Shows the 6 highest-value tabs inline (Tasks,
 * Dashboard, Overview, Milestones, Meetings, Billing when granted) and
 * tucks every other granted tab into a "More ▾" dropdown so the strip
 * stays on one line even on mobile viewports. The active tab is always
 * surfaced inline — even if it would normally live under More — so
 * users never lose track of where they are.
 *
 * Feb 2026 R293p · Added per-user Pin Favourites. A pin icon on each
 * row of the "More" dropdown lets each PM promote a tab into the
 * inline strip; their choice is persisted in localStorage so the next
 * project they open honours their reading order. */
const PRIMARY_TAB_VALUES = ["tasks", "dashboard", "overview", "milestones", "meetings", "billing"];
const PINNED_TABS_KEY = "co-project-tabs-pinned-v1";

function readPinnedTabs() {
  try {
    const raw = JSON.parse(localStorage.getItem(PINNED_TABS_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((v) => typeof v === "string") : [];
  } catch { return []; }
}

function writePinnedTabs(values) {
  try { localStorage.setItem(PINNED_TABS_KEY, JSON.stringify(values)); }
  catch { /* ignore */ }
}

function ProjectTabsBar({ tabs, activeTab, onTabChange }) {
  const [pinned, setPinned] = useState(() => readPinnedTabs());
  // Feb 2026 R293p · Controlled "More" menu so it closes after the
  // user picks a tab (plain <button> rows don't fire Radix onSelect).
  const [moreOpen, setMoreOpen] = useState(false);
  const togglePin = (value) => {
    setPinned((cur) => {
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      writePinnedTabs(next);
      return next;
    });
  };

  const primary = [];
  const overflow = [];
  // First pass — split by priority bucket, respecting the user's grant
  // list. A user-pinned tab trumps the default bucket.
  for (const t of tabs) {
    const isDefaultPrimary = PRIMARY_TAB_VALUES.includes(t.value);
    const isUserPinned = pinned.includes(t.value);
    if (isDefaultPrimary || isUserPinned) primary.push(t);
    else overflow.push(t);
  }
  // Keep primary rendered with default-primaries first (in canonical
  // order) and user-pinned tabs after, so the eye doesn't jump around
  // when a user adds a pin.
  primary.sort((a, b) => {
    const aDef = PRIMARY_TAB_VALUES.indexOf(a.value);
    const bDef = PRIMARY_TAB_VALUES.indexOf(b.value);
    if (aDef !== -1 && bDef !== -1) return aDef - bDef;
    if (aDef !== -1) return -1;
    if (bDef !== -1) return 1;
    // Both user-pinned → preserve pin insertion order.
    return pinned.indexOf(a.value) - pinned.indexOf(b.value);
  });

  // Promote the active tab inline even if it would normally live under
  // More, so the active indicator never hides inside a dropdown.
  const activeInOverflow = overflow.find((t) => t.value === activeTab);
  const visibleInline = activeInOverflow
    ? [...primary, activeInOverflow]
    : primary;
  const dropdownTabs = activeInOverflow
    ? overflow.filter((t) => t.value !== activeTab)
    : overflow;

  return (
    <TabsList
      className="rounded-none mb-3 h-auto p-0 bg-transparent border-b border-border gap-0 flex-wrap"
      data-testid="project-tabs-bar"
    >
      {visibleInline.map((t) => {
        const isPinned = pinned.includes(t.value);
        const isDefaultPrimary = PRIMARY_TAB_VALUES.includes(t.value);
        return (
          <div key={t.value} className="relative group inline-flex items-center">
            <TabsTrigger
              value={t.value}
              className={`text-xs px-3 py-2 ${isPinned && !isDefaultPrimary ? "pr-6" : ""} rounded-none data-[state=active]:bg-background data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:font-semibold border-b-2 border-transparent hover:bg-muted/30 transition-colors`}
              data-testid={`project-tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
            {/* Unpin affordance — only shown on user-pinned tabs
                 (default primaries can't be unpinned so we don't offer
                 a misleading button). */}
            {isPinned && !isDefaultPrimary && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); togglePin(t.value); }}
                className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-primary hover:text-primary/80"
                aria-label={`Unpin ${t.label}`}
                title={`Unpin ${t.label}`}
                data-testid={`project-tab-unpin-${t.value}`}>
                <PinOff className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
      {dropdownTabs.length > 0 && (
        <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-none hover:bg-muted/30 text-muted-foreground border-b-2 border-transparent"
              data-testid="project-tabs-more">
              More
              <MoreHorizontal className="w-3.5 h-3.5" />
              <span className="inline-block min-w-[18px] text-center bg-muted text-foreground text-[9px] font-bold px-1 py-0 rounded-none ml-0.5">
                {dropdownTabs.length}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="rounded-none min-w-[240px]">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
              <span>More sections</span>
              <span className="text-[9px] font-normal normal-case tracking-normal text-muted-foreground/70">
                Click <Pin className="inline w-2.5 h-2.5" /> to pin
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {dropdownTabs.map((t) => (
              <div
                key={t.value}
                className="flex items-center hover:bg-muted/40"
                data-testid={`project-tab-more-${t.value}`}>
                <button
                  type="button"
                  onClick={() => { onTabChange(t.value); setMoreOpen(false); }}
                  className="flex-1 text-left text-xs px-2 py-1.5 cursor-pointer">
                  {t.label}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); togglePin(t.value); }}
                  className="px-2 py-1.5 text-muted-foreground hover:text-primary"
                  aria-label={`Pin ${t.label} to the tab strip`}
                  title={`Pin ${t.label} to the tab strip`}
                  data-testid={`project-tab-pin-${t.value}`}>
                  <Pin className="w-3 h-3" />
                </button>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </TabsList>
  );
}
